import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { FieldLabel } from 'components/UI/FieldLabel';
import { PickerListItem } from 'components/UI/PickerListItem';
import { FilterSheetFooter } from 'components/UI/FilterSheetFooter';
import { FILTER_PANEL_CONTENT_SX } from 'components/UI/filterPanelSx';
import {
  CertificatesDateRangeFilter,
  type DateRangeKey,
} from 'components/pages/certificates/CertificatesDateRangeFilter';
import { MobileDateRangeDrawer } from './MobileDateRangeDrawer';
import { ACTIVITY_TYPE_GROUPS, getSelectedGroupKeys } from './activityTypes';
import { useAutoApply } from 'hooks/useAutoApply';

export interface ActivityFilterValues {
  /** Raw action-log types, expanded from the selected groups. */
  types: string[];
  dateFrom: string;
  dateTo: string;
  selectedRange: DateRangeKey;
}

interface ActivityFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: ActivityFilterValues) => void;
  initialValues?: Partial<ActivityFilterValues>;
  /**
   * Show the WHEN axis. Default true.
   *
   * The cross-certificate feed turns it off, and that is a correctness call
   * rather than a layout preference: that feed filters on the SERVER (it pages
   * through hundreds of events, so filtering the loaded window would misreport
   * counts), and the endpoint takes types but not a date range yet. Offering a
   * date control that could only be honoured over the loaded window would be
   * the exact lie server-side filtering was chosen to avoid — so the axis is
   * absent until the API carries it, rather than present and quietly partial.
   */
  showDateRange?: boolean;
}

/**
 * Filter control for the Activity timeline (VERA-978).
 *
 * Two axes, matching what the timeline actually shows: WHAT happened (event type)
 * and WHEN. Event types are offered as groups rather than the ~21 raw action-log
 * types (see ACTIVITY_TYPE_GROUPS) — eleven of those are transfer states nobody
 * filters on individually. The date control is the same one the Scans filter uses,
 * so both tabs read and behave identically.
 */
export const ActivityFilterDrawer: React.FC<ActivityFilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  initialValues,
  showDateRange = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>(initialValues?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState<string>(initialValues?.dateTo ?? '');
  const [selectedRange, setSelectedRange] = useState<DateRangeKey>(
    initialValues?.selectedRange ?? null
  );
  const [dateOpen, setDateOpen] = useState<boolean>(false);

  // The drawer stays mounted between opens, so its local state has to be re-seeded
  // from the applied values each time it opens. Without this, clearing filters from
  // the bar would leave the drawer showing the old selection next time it's opened.
  useEffect(() => {
    if (!open) return;
    setSelectedGroups(getSelectedGroupKeys(initialValues?.types ?? []));
    setDateFrom(initialValues?.dateFrom ?? '');
    setDateTo(initialValues?.dateTo ?? '');
    setSelectedRange(initialValues?.selectedRange ?? null);
    // Re-seeding is keyed on the open transition only. Depending on initialValues would
    // re-run on every parent render and fight the user's in-drawer edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const expandGroups = (groups: string[]): string[] =>
    ACTIVITY_TYPE_GROUPS.filter(group => groups.includes(group.key)).flatMap(group => group.types);

  /*
   * AUTO-APPLY — no Apply button, at either breakpoint. Every control commits on a
   * 400ms debounce, the same model and the same figure as the platform's filter
   * drawer. See `useAutoApply` for why the schedule hangs off the interaction and
   * not off a state-watching effect: this sheet RE-SEEDS all four of its fields
   * from `initialValues` on the open transition, and a watcher would read that
   * re-seed as an edit and bounce the applied values straight back on every open.
   *
   * `open` is passed so a change made in the last 400ms before dismissal is
   * FLUSHED rather than dropped — this drawer stays mounted between opens, so it
   * never gets an unmount to flush on.
   */
  const { schedule } = useAutoApply(
    () =>
      onApply({
        types: expandGroups(selectedGroups),
        dateFrom,
        dateTo,
        selectedRange,
      }),
    open,
  );

  const toggleGroup = (key: string) => {
    setSelectedGroups(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    schedule();
  };

  const clearDate = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedRange(null);
    schedule();
  };

  const footer = (
    <FilterSheetFooter
      /* `panel` on desktop: right-aligned, intrinsic-width, 40px — the same footer
         geometry the PLATFORM's filter drawer renders from this same component. A 50/50
         pair of 44px thumb targets is a phone-sheet shape and read as one on a 400px
         pointer-driven side panel. `sheet` on mobile is unchanged. */
      mode={isMobile ? 'sheet' : 'panel'}
      /* RESET ONLY. Omitting `onApply` is how the shared component's auto-apply mode
         is selected, and `count` went with the button it labelled. Reset still does
         not close the sheet, and it now commits — clearing IS the change. */
      onReset={() => {
        setSelectedGroups([]);
        clearDate();
      }}
    />
  );

  const content = (
    // DrawerPopup pads its child horizontally only for bottom sheets, so the desktop
    // side panel supplies its own gutter here.
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: isMobile ? 0 : 2 }}>
      {/* `minHeight: 0` is what makes `overflowY: auto` actually scroll. A flex item
          defaults to `min-height: auto` — sized to its CONTENT, not shrinkable to the
          space `flex: 1` assigns it — so without this the body grows past its share,
          the scroll never engages, and the footer below is pushed out of the panel
          instead of staying pinned to its bottom. */}
      <Stack sx={{ gap: 3, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {/*
          Activity type.

          These are FIELD labels — one label naming exactly ONE control — not
          section headings, so they converge on `components/UI/FieldLabel`,
          which owns the typography, the 8px label→control gap, AND the `action`
          slot for the right-aligned Clear these rows used to hand-roll with
          their own space-between Stack.

          That convergence replaced FIVE different hand-rolled treatments across
          the filter sheets: `fontSize: 14 / fontWeight: 500 / text.primary`
          Typography (here, ScansFilterDrawer, ProvenanceFilter), a bare
          `<Typography>` (CertificatesDateRangeFilter desktop, CertificatesFilter
          desktop), and two Tailwind `text-md font-medium` variants (the same two
          files' mobile branches, one of them with `mb-3`).

          "Activity type", not "Event type": the tab, the sheet and the feed are
          all called Activity, and "Event" appeared nowhere else in the UI — so
          it asked the reader to map a second word onto a concept they already
          have a name for. The sibling sheets each carry their own noun the same
          way ("Scan date", "Ownership date").
        */}
        <Stack sx={{ gap: 1 }}>
          <FieldLabel
            label='Activity type'
          />
          {/* PickerListItem, not a hand-rolled checkbox row (VERA-978). It is the app's
              shared "selectable row with a trailing check" primitive — extracted per
              working-conventions §36 so item height, radius and the selected treatment
              come from pickerListStyle.ts rather than drifting per drawer. Multi-select
              here is just "more than one row carries `selected`". */}
          <Stack spacing={0}>
            {ACTIVITY_TYPE_GROUPS.map(group => (
              <PickerListItem
                key={group.key}
                label={group.label}
                selected={selectedGroups.includes(group.key)}
                onClick={() => toggleGroup(group.key)}
              />
            ))}
          </Stack>
        </Stack>

        {/* Date */}
        {showDateRange && (
        <Stack sx={{ gap: 1 }}>
          {/* One label for one field (VERA-978). The section heading said "Date" and the
              range control then rendered its own "Activity date" directly beneath it. The
              heading stays (it matches the Activity type section above and ScansFilterDrawer's
              own Date section); the control's label is suppressed with fieldLabel=''. Its
              Clear link lived inside that suppressed row, so it moves up here — the same
              label-row Clear the Activity type section uses. */}
          <FieldLabel
            label='Date'
          />
          <CertificatesDateRangeFilter
            mobile={isMobile}
            selectedRange={selectedRange}
            selectedDateFrom={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
            selectedDateTo={dateTo ? new Date(dateTo + 'T00:00:00') : null}
            open={dateOpen}
            onOpen={() => setDateOpen(true)}
            onClose={() => setDateOpen(false)}
            onClear={clearDate}
            /* Both of these are COMPLETE states, so both commit at once: a preset
               names its own range, and this control picks a single `dateFrom` with
               `dateTo` deliberately empty ("from this date onwards"). There is no
               half-expressed range to guard against here — unlike the certificates
               sheet, whose mobile calendar is `mode='range'` and whose first tap
               produces a `from` with no `to`. */
            onSelectRange={range => {
              setSelectedRange(range);
              setDateFrom('');
              setDateTo('');
              schedule();
            }}
            onSelectDate={date => {
              setSelectedRange(null);
              setDateFrom(date ? date.toISOString().slice(0, 10) : '');
              setDateTo('');
              schedule();
            }}
            fieldLabel=''
          />

          {isMobile && (
            <MobileDateRangeDrawer
              open={dateOpen}
              title='Activity date'
              selectedRange={selectedRange}
              selectedDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
              onSelectRange={range => {
                setSelectedRange(range);
                setDateFrom('');
                setDateTo('');
                schedule();
              }}
              onSelectDate={date => {
                setSelectedRange(null);
                setDateFrom(date ? date.toISOString().slice(0, 10) : '');
                setDateTo('');
                schedule();
              }}
              onClear={clearDate}
              onDone={() => setDateOpen(false)}
              onClose={() => setDateOpen(false)}
            />
          )}
        </Stack>
        )}
      </Stack>

      {footer}
    </Stack>
  );

  return (
    <DrawerPopup
      title='Filters'
      /* NO 'Cancel' — the header X, which is what DrawerPopup draws when no right
         action is given (and what this drawer's desktop branch already showed).
         Under auto-apply nothing is pending, so "Cancel" would promise a revert
         that cannot happen; Reset clears to empty, which is not the same thing. */
      anchor={isMobile ? 'bottom' : 'right'}
      child={content}
      handleOpen={open}
      handleClose={onClose}
      /* Desktop only — makes the content box FILL the panel so the footer's
         `marginTop: auto` has space to push Reset/Apply to the bottom. See
         `FILTER_PANEL_CONTENT_SX` for why `height: 100%` alone did not. */
      contentSx={isMobile ? undefined : FILTER_PANEL_CONTENT_SX}
      /*
       * Mobile bottom sheet is content-height, never full-page (VERA-294 rule).
       * DESKTOP FILLS — the side panel is full viewport height less its 16px insets,
       * matching the certificates filter drawer and the PLATFORM's, whose scroll body
       * is pinned by `minHeight: vhMinus(172)` AND `maxHeight: vhMinus(172)`.
       *
       * This was briefly `height: 'auto'` because five short rows left dead air below
       * Apply. That reasoning was wrong: a side panel is CHROME, and chrome that
       * resizes with its contents reads as unstable — it jumps as filters are added or
       * a section expands, and two filter drawers in the same app end up different
       * heights for no reason a user can see. The platform fills for that reason, not
       * because it happens to hold more rows.
       *
       * `height` must be STATED — `.MuiDrawer-paper` sets `height: 100%`, so the inset
       * has to be subtracted explicitly.
       *
       * 12px, not 32px, and matching the certificates drawer exactly. 32 was the outlier
       * of every panel in the app (certificates drawer 12, sort menu 8, layout popover
       * 16). Not a collision fix — measured, a 32px corner needs only 9.4px of padding
       * to clear its curve (R*(1 - 1/sqrt(2))) and the header already has 16 — purely
       * that two sibling filter drawers must not have different corners.
       */
      paperSx={
        isMobile
          ? { height: 'auto', maxHeight: '85vh' }
          : {
              width: 400,
              maxWidth: '80vw',
              mr: 2,
              my: 2,
              height: 'calc(100% - 32px)',
              borderRadius: '12px',
            }
      }
    />
  );
};

export default ActivityFilterDrawer;
